import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem, Avatar,
  CircularProgress, InputAdornment, Paper, Stack, Container, IconButton
} from '@mui/material';
import {
  Person, Event, LocationCity, LocalPostOffice,
  Email, PhoneIphone, CameraAlt, VerifiedUser, Home
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext'; // 👈 AuthContext इम्पोर्ट किया
import { useNavigate } from 'react-router-dom';

const ProfileCompletion = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [mobile, setMobile] = useState('Loading...');
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    state: '',
    district: '',
    pincode: '',
    address: '',
    email: ''
  });

  const { getProfile, updateProfile } = useAuth(); // 👈 getProfile को निकाला

  // --- डेटा सेट करने वाला लॉजिक यहाँ है ---
  useEffect(() => {
    const fetchProfileInfo = async () => {
      try {
        setFetching(true);
        const response = await getProfile();
        console.log("Profile Data:", response);

        const customer = response?.data || response;

        if (customer) {
          // मोबाइल नंबर सेट करना
          setMobile(`+${customer.country_code || '91'} ${customer.mobile || ''}`);

          // फॉर्म का सारा डेटा एक साथ सेट करना
          setFormData({
            name: customer.name || '',
            dob: customer.dob ? customer.dob.split('T')[0] : '', // Date format fix (YYYY-MM-DD)
            state: customer.state || '',
            district: customer.district || '',
            pincode: customer.pincode || '',
            address: customer.address || '',
            email: customer.email || ''
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchProfileInfo();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      dob: formData.dob ? formData.dob : null,
      pincode: formData.pincode ? parseInt(formData.pincode) : null,
      mobile: String(formData.mobile).replace(/\D/g, ''),
    };

    console.log("Updating all fields with payload:", payload);

    setLoading(true);
    try {
      const response = await updateProfile(payload);

      if (response?.success) {
        alert("प्रोफ़ाइल पूरी तरह से अपडेट हो गई है! ✅");
        navigate('/my-profile');
      } else {
        alert(response?.message || "अपडेट फेल हो गया।");
      }
    } catch (error) {
      console.error("Critical Update Error:", error);
      alert("सर्वर से संपर्क नहीं हो पाया।");
    } finally {
      setLoading(false);
    }
  };

  
  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA', pb: 5 }}>
      {/* Premium Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #FF512F 0%, #DD2476 100%)',
        height: '200px',
        borderBottomLeftRadius: '40px',
        borderBottomRightRadius: '40px',
        display: 'flex',
        justifyContent: 'center',
        pt: 4
      }}>
        <Typography variant="h6" color="white" fontWeight="600">Edit Profile</Typography>
      </Box>

      <Container maxWidth="xs" sx={{ mt: '-80px' }}>
        <Paper elevation={10} sx={{ p: 3, borderRadius: '24px' }}>
          {/* Avatar Section */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar sx={{ width: 90, height: 90, bgcolor: '#f0f0f0', border: '4px solid white', boxShadow: 3 }}>
                <Person sx={{ fontSize: 50, color: '#bdbdbd' }} />
              </Avatar>
              <IconButton size="small" sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'white', boxShadow: 2 }}>
                <CameraAlt sx={{ fontSize: 16, color: '#DD2476' }} />
              </IconButton>
            </Box>
            <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>प्रोफ़ाइल पूरी करें</Typography>
            <Typography variant="caption" color="textSecondary">Complete Profile • Fill your details</Typography>
          </Box>

          <Stack spacing={2.5}>
            {/* 1. Name */}
            <TextField
              fullWidth
              label="पूरा नाम / Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start"><Person color="primary" /></InputAdornment>,
                sx: { borderRadius: '15px' }
              }}
            />

            {/* 2. DOB */}
            <TextField
              fullWidth
              label="जन्म तिथि / Date of Birth"
              name="dob"
              type="date"
              value={formData.dob}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Event color="primary" /></InputAdornment>,
                sx: { borderRadius: '15px' }
              }}
            />

            {/* 3. State & District */}
            <Stack direction="row" spacing={2}>
              <TextField
                select
                fullWidth
                label="राज्य / State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                InputProps={{ sx: { borderRadius: '15px' } }}
              >
                <MenuItem value="Uttar Pradesh">Uttar Pradesh</MenuItem>
                <MenuItem value="Bihar">Bihar</MenuItem>
                <MenuItem value="Delhi">Delhi</MenuItem>
                <MenuItem value="Madhya Pradesh">Madhya Pradesh</MenuItem>
              </TextField>

              <TextField
                fullWidth
                label="जिला / District"
                name="district"
                value={formData.district}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LocationCity fontSize="small" color="primary" /></InputAdornment>,
                  sx: { borderRadius: '15px' }
                }}
              />
            </Stack>

            {/* 4. Pincode */}
            <TextField
              fullWidth
              label="पिन कोड / Pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocalPostOffice color="primary" /></InputAdornment>,
                sx: { borderRadius: '15px' }
              }}
            />

            {/* 5. Email */}
            <TextField
              fullWidth
              label="ईमेल / Email (Optional)"
              name="email"
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Email color="primary" /></InputAdornment>,
                sx: { borderRadius: '15px' }
              }}
            />

            {/* 6. Address */}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="पता / Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Home color="primary" /></InputAdornment>,
                sx: { borderRadius: '15px' }
              }}
            />

            {/* Mobile Display */}
            <Box sx={{ p: 2, bgcolor: '#F0F9FF', borderRadius: '15px', border: '1px solid #BAE6FD', display: 'flex', alignItems: 'center', gap: 2 }}>
              <PhoneIphone color="primary" />
              <Box flexGrow={1}>
                <Typography variant="caption" color="textSecondary">Mobile Number</Typography>
                <Typography variant="body2" fontWeight="700">{mobile}</Typography>
              </Box>
              <VerifiedUser sx={{ color: '#0EA5E9', fontSize: 20 }} />
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              sx={{
                py: 2,
                borderRadius: '15px',
                background: 'linear-gradient(90deg, #FF512F 0%, #DD2476 100%)',
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: '0 8px 20px rgba(221, 36, 118, 0.3)',
                '&:hover': { opacity: 0.9 }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'COMPLETE PROFILE'}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProfileCompletion;